import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementPageCard } from '../layout/ManagementPageLayout';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { useProUpgrade } from '../../context/ProUpgradeContext';
import ProCrownIcon from '../billing/ProCrownIcon';

const folderTabActiveBg = BRAND.surface;
const folderTabActiveColor = BRAND.accentText;
const folderTabInactiveBg = brandAlpha('text', 0.04);
const folderTabInactiveHoverBg = brandAlpha('text', 0.08);
const folderTabInactiveColor = BRAND.textMuted;
const folderTabBorder = BRAND.line;
const folderTabIndicator = `inset 0 2px 0 ${BRAND.accent}`;
const folderTabDivider = BRAND.line;

const overflowButtonSx = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: 4,
  transform: 'translateY(-50%)',
  zIndex: 3,
  width: 28,
  height: 28,
  bgcolor: BRAND.surface,
  color: BRAND.accentText,
  border: `1px solid ${BRAND.line}`,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: brandAlpha('accent', 0.08),
    color: BRAND.accentText,
  },
});

const overflowFadeSx = (side) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [side]: 0,
  width: 40,
  zIndex: 2,
  pointerEvents: 'none',
  background: side === 'left'
    ? `linear-gradient(to right, ${brandAlpha('surface', 0.97)}, ${brandAlpha('surface', 0)})`
    : `linear-gradient(to left, ${brandAlpha('surface', 0.97)}, ${brandAlpha('surface', 0)})`,
});

const FolderTabs = ({
  tabs,
  value,
  onChange,
  children,
  contentId,
  contentSx,
  equalWidthOnMobile = false,
}) => {
  const { t } = useTranslation();
  const { openProUpgrade } = useProUpgrade();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tabListRef = useRef(null);
  const activeTabRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const movedByKeyboardRef = useRef(false);

  const updateOverflow = useCallback(() => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return;
    }

    const maxScroll = tabList.scrollWidth - tabList.clientWidth;
    setCanScrollLeft(tabList.scrollLeft > 1);
    setCanScrollRight(maxScroll - tabList.scrollLeft > 1);
  }, []);

  useLayoutEffect(() => {
    updateOverflow();
  }, [tabs, value, updateOverflow]);

  useEffect(() => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return undefined;
    }

    updateOverflow();
    tabList.addEventListener('scroll', updateOverflow, { passive: true });
    window.addEventListener('resize', updateOverflow);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateOverflow);
      resizeObserver.observe(tabList);
    }

    return () => {
      tabList.removeEventListener('scroll', updateOverflow);
      window.removeEventListener('resize', updateOverflow);
      resizeObserver?.disconnect();
    };
  }, [updateOverflow]);

  useEffect(() => {
    const tabList = tabListRef.current;
    const activeTab = activeTabRef.current;
    if (!tabList || !activeTab) {
      return;
    }

    const tabListRect = tabList.getBoundingClientRect();
    const activeTabRect = activeTab.getBoundingClientRect();
    const leftOverflow = activeTabRect.left - tabListRect.left;
    const rightOverflow = activeTabRect.right - tabListRect.right;

    if (leftOverflow < 0) {
      tabList.scrollBy({ left: leftOverflow - 12, behavior: 'smooth' });
    } else if (rightOverflow > 0) {
      tabList.scrollBy({ left: rightOverflow + 12, behavior: 'smooth' });
    }

    if (movedByKeyboardRef.current) {
      activeTab.focus({ preventScroll: true });
      movedByKeyboardRef.current = false;
    }
  }, [value]);

  const scrollTabs = (direction) => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return;
    }

    const distance = Math.max(120, tabList.clientWidth * 0.6);
    tabList.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  const handleTabKeyDown = (event, tabValue) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }

    event.preventDefault();
    const currentIndex = tabs.findIndex((tab) => tab.value === tabValue);
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }

    const nextTab = tabs[nextIndex];
    if (nextTab && nextTab.value !== tabValue) {
      if (nextTab.proLocked) {
        openProUpgrade();
        return;
      }
      movedByKeyboardRef.current = true;
      onChange(nextTab.value);
    }
  };

  return (
    <ManagementPageCard
      id={contentId}
      sx={{
        overflow: 'hidden',
        p: 0,
        ...contentSx,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {canScrollLeft && <Box aria-hidden sx={overflowFadeSx('left')} />}
        {canScrollRight && <Box aria-hidden sx={overflowFadeSx('right')} />}
        {canScrollLeft && (
          <IconButton
            size="small"
            aria-label={t('common.previousTabs')}
            onClick={() => scrollTabs(-1)}
            sx={overflowButtonSx('left')}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
        {canScrollRight && (
          <IconButton
            size="small"
            aria-label={t('common.nextTabs')}
            onClick={() => scrollTabs(1)}
            sx={overflowButtonSx('right')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: folderTabDivider,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <Box
          ref={tabListRef}
          role="tablist"
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            flexWrap: 'nowrap',
            gap: { xs: 0.5, sm: 0.75 },
            px: { xs: 1.5, sm: 3 },
            pt: 1.5,
            // Room for the active tab to cover the baseline without leaving the scrollport.
            pb: '1px',
            position: 'relative',
            zIndex: 1,
            width: '100%',
            overflowX: 'auto',
            overscrollBehaviorX: 'contain',
            touchAction: 'pan-x',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {tabs.map((tab) => {
            const isActive = tab.value === value;
            const label = isMobile && tab.mobileLabel ? tab.mobileLabel : tab.label;

            return (
              <Box
                key={tab.value}
                ref={isActive ? activeTabRef : undefined}
                role="tab"
                aria-selected={isActive}
                aria-label={tab.label}
                component="button"
                type="button"
                tabIndex={isActive ? 0 : -1}
                onClick={() => {
                  if (tab.proLocked) {
                    openProUpgrade();
                    return;
                  }
                  onChange(tab.value);
                }}
                onKeyDown={(event) => handleTabKeyDown(event, tab.value)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                  appearance: 'none',
                  borderTop: '1px solid',
                  borderRight: '1px solid',
                  borderLeft: '1px solid',
                  borderBottom: 0,
                  borderColor: isActive ? folderTabBorder : 'transparent',
                  fontFamily: 'inherit',
                  fontSize: { xs: '0.75rem', sm: '0.95rem' },
                  lineHeight: 1.3,
                  px: { xs: equalWidthOnMobile ? 0.75 : 1.5, sm: 3 },
                  py: { xs: 1, sm: 1.25 },
                  borderTopLeftRadius: BRAND_RADIUS.button,
                  borderTopRightRadius: BRAND_RADIUS.button,
                  bgcolor: isActive ? folderTabActiveBg : folderTabInactiveBg,
                  boxShadow: isActive ? folderTabIndicator : 'none',
                  color: isActive ? folderTabActiveColor : folderTabInactiveColor,
                  fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                  position: 'relative',
                  zIndex: isActive ? 2 : 1,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  ...(equalWidthOnMobile
                    ? {
                        flexGrow: { xs: 1, sm: 0 },
                        flexBasis: 'auto',
                        minWidth: 'max-content',
                      }
                    : {
                        minWidth: 0,
                      }),
                  '&:hover': {
                    bgcolor: isActive ? folderTabActiveBg : folderTabInactiveHoverBg,
                    color: isActive ? folderTabActiveColor : BRAND.text,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${BRAND.accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {label}
                {isActive ? (
                  <Box
                    component="span"
                    aria-hidden
                    data-testid="folder-tab-baseline-cover"
                    sx={{
                      position: 'absolute',
                      left: '-1px',
                      right: '-1px',
                      bottom: '-1px',
                      height: '1px',
                      bgcolor: folderTabActiveBg,
                      pointerEvents: 'none',
                    }}
                  />
                ) : null}
                {tab.proLocked ? (
                  <Box component="span" sx={{ display: 'inline-flex', ml: 0.25 }}>
                    <ProCrownIcon size={11} />
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: folderTabActiveBg,
          position: 'relative',
          zIndex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </ManagementPageCard>
  );
};

export default FolderTabs;
